import { Injectable } from '@angular/core';

import { SnLonLat, SnLonLatLike } from '@lib/geo/helpers';
import { IMarker as _IMarker, MapMarkerIcon, IMapMarkerStyleOptions, IMapMarkerOptions } from '@lib/geo/map';

import { GoogleMap } from '../../../map';
import { IGoogleMarker } from '../interfaces/google-marker.interface';
import { IGoogleMapMarkerLabel } from '../marker-options';
import { GoogleMapMarkerType } from '../marker-types';

type IMarker = _IMarker<GoogleMap>;

/**
 * Service responsible for style operations on Google Map markers.
 * Handles visual appearance, icons, colors, and other styling properties.
 * Note: Badge-related functionality is handled by GoogleMarkerBadgeService.
 */
@Injectable()
export class GoogleMarkerStyleService {
  setStyle(googleMarker: IGoogleMarker, marker: IMarker, style: IMapMarkerStyleOptions): void {
    if (style.rotation !== undefined) {
      googleMarker.setRotation(style.rotation);
    }

    marker.styles = { ...marker.styles, ...style };
  }

  setOptions(
    googleMarker: IGoogleMarker,
    marker: IMarker,
    options: Partial<IMapMarkerOptions>,
  ): void {
    if (options.draggable !== undefined) {
      googleMarker.setDraggable(options.draggable);
    }

    if (options.zIndex !== undefined) {
      googleMarker.setZIndex(options.zIndex);
    }

    if (options.title !== undefined) {
      googleMarker.setTitle(options.title);
    }

    if (options.draggable !== undefined) {
      marker.draggable = options.draggable;
    }
    if (options.title !== undefined) {
      marker.title = options.title;
    }
  }

  setIcon(googleMarker: IGoogleMarker, marker: IMarker, icon: MapMarkerIcon): void {
    googleMarker.setIcon(icon);
    marker.icon = icon;
  }

  setZIndex(googleMarker: IGoogleMarker, marker: IMarker, zIndex: number): void {
    googleMarker.setZIndex(zIndex);
  }

  setDraggable(googleMarker: IGoogleMarker, marker: IMarker, draggable: boolean): void {
    googleMarker.setDraggable(draggable);
    marker.draggable = draggable;
  }

  setRotation(googleMarker: IGoogleMarker, marker: IMarker, rotation: number): void {
    googleMarker.setRotation(rotation);
    marker.rotation = rotation;
  }

  setTitle(googleMarker: IGoogleMarker, marker: IMarker, title: string): void {
    googleMarker.setTitle(title);
    marker.title = title;
  }

  getStyle(marker: IMarker): IMapMarkerStyleOptions {
    return marker.styles || {};
  }

  getIcon(marker: IMarker): MapMarkerIcon | undefined {
    return marker.icon;
  }

  getZIndex(googleMarker: IGoogleMarker): number {
    return googleMarker.getZIndex();
  }

  getTitle(marker: IMarker): string {
    return marker.title;
  }

  isDraggable(marker: IMarker): boolean {
    return marker.draggable;
  }

  getRotation(marker: IMarker): number {
    return marker.rotation;
  }

  // ===== ADVANCED MARKER PROPERTY METHODS =====

  setType(googleMarker: IGoogleMarker, marker: IMarker, type: GoogleMapMarkerType): void {
    googleMarker.setType(type);
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.type = type.toString();
  }

  getType(googleMarker: IGoogleMarker): GoogleMapMarkerType {
    return googleMarker.getType();
  }

  getFeatures(googleMarker: IGoogleMarker): unknown {
    return googleMarker.getFeatures();
  }

  getIconLabel(googleMarker: IGoogleMarker): unknown {
    return googleMarker.iconLabel;
  }

  setLabel(
    googleMarker: IGoogleMarker,
    marker: IMarker,
    label: string | IGoogleMapMarkerLabel,
  ): void {
    googleMarker.setLabel(label);
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.label = typeof label === 'string' ? { text: label } : label;
  }

  getLabel(googleMarker: IGoogleMarker): IGoogleMapMarkerLabel {
    return googleMarker.getLabel();
  }

  setPulse(
    googleMarker: IGoogleMarker,
    marker: IMarker,
    colors: string[],
    delay: number,
    fontSet?: string,
    fontIcon?: string,
  ): void {
    googleMarker.setPulse(colors, delay, fontSet, fontIcon);
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.pulseAnimation = {
      colors,
      delay,
      fontSet,
      fontIcon,
      active: true,
    };
  }

  stopPulse(googleMarker: IGoogleMarker, marker: IMarker): void {
    if (googleMarker.pulseInterval) {
      clearInterval(googleMarker.pulseInterval);
      googleMarker.pulseInterval = null;
    }
    if (marker.metadata?.pulseAnimation) {
      marker.metadata.pulseAnimation.active = false;
    }
  }

  getPulseState(marker: IMarker): { active: boolean; colors?: string[]; delay?: number } {
    return marker.metadata?.pulseAnimation || { active: false };
  }

  setAddress(googleMarker: IGoogleMarker, marker: IMarker, address: string): void {
    googleMarker.address = address;
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.address = address;
  }

  getAddress(googleMarker: IGoogleMarker): string {
    return googleMarker.address;
  }

  setFeature(googleMarker: IGoogleMarker, marker: IMarker, feature: unknown): void {
    googleMarker.feature = feature as any;
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.feature = feature;
  }

  getFeature(googleMarker: IGoogleMarker): unknown {
    return googleMarker.feature;
  }

  setIconAnchor(googleMarker: IGoogleMarker, marker: IMarker, anchor: SnLonLatLike): void {
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.iconAnchor = SnLonLat.convert(anchor);
  }

  getIconAnchor(marker: IMarker): SnLonLat | undefined {
    return marker.metadata?.iconAnchor;
  }

  setCustomData(googleMarker: IGoogleMarker, marker: IMarker, data: unknown): void {
    if (!marker.metadata) marker.metadata = {};
    marker.metadata.customData = data as Record<string, unknown>;
  }

  getCustomData(marker: IMarker): unknown {
    return marker.metadata?.customData;
  }
}
